<template>
  <div class="candidate-component">
    <div class="service-header mb-2">
      <h6 class="fs-6 fw-semibold text-dark mb-1 mt-0">
        Candidate Information
      </h6>
      <p class="text-xs text-muted mb-0 lh-sm">
        Personal details required for all background checks and identity
        verification.
      </p>
    </div>

    <div class="row g-2">
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >First Name <span class="text-danger">*</span></label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter first name"
          id="first-name-field"
          v-model="candidate_data.first_name"
          @input="allow_character"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >Surname <span class="text-danger">*</span></label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter surname"
          id="surname-field"
          v-model="candidate_data.surname"
          @input="allow_character"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >ID Number <span class="text-danger">*</span></label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter ID number"
          v-model="candidate_data.id_number"
          @input="allow_num"
          maxlength="13"
          id="id-number-field"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >Date of Birth <span class="text-danger">*</span></label
        >
        <input
          type="date"
          class="form-control form-control-sm text-xs"
          v-model="candidate_data.date_of_birth"
          id="date-of-birth-field"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary">
          Race
          <span v-if="criminal_check_included || academic_checks_included" class="text-danger">*</span>
          <span v-else class="text-muted">(Optional)</span>
        </label>
        <select
          class="form-control form-control-sm text-xs"
          v-model="candidate_data.race_code"
          id="race-code-field"
        >
          <option value="" disabled>Select race</option>
          <option value="1">Black</option>
          <option value="2">White</option>
          <option value="3">Coloured</option>
          <option value="4">Asian</option>
          <option value="5">Other</option>
          <option value="6">Black and Coloured</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >Maiden Name (Optional)</label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter maiden name"
          id="maiden-name-field"
          v-model="candidate_data.maiden_name"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >Other Names (Optional)</label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter other names"
          id="other-names-field"
          v-model="candidate_data.other_names"
          @input="allow_character"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label text-xs fw-medium text-secondary"
          >Passport Number (Optional)</label
        >
        <input
          type="text"
          class="form-control form-control-sm text-xs"
          placeholder="Enter passport number"
          v-model="candidate_data.passport_number"
          id="passport-number-field"
          maxlength="20"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  allow_num,
  allow_character,
  validate_id,
} from "@/utils/validations.js";
import { API_BASE_URL } from "@/config";

export default {
  name: "CandidateComponent",
  props: {
    service_id: {
      type: Number,
      required: true,
    },
    criminal_check_included: {
      type: Boolean,
      default: false,
    },
    academic_checks_included: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      candidate_data: {
        id: "",
        first_name: "",
        surname: "",
        date_of_birth: "",
        id_number: "",
        maiden_name: "",
        other_names: "",
        passport_number: "",
        race_code: "",
      },
    };
  },
  methods: {
    validate_id,
    allow_num,
    allow_character,
    validate_first_name() {
      if (!this.candidate_data.first_name) {
        this.set_field_invalid("first-name-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "First name is required.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid("first-name-field");
      return true;
    },
    validate_surname() {
      if (!this.candidate_data.surname) {
        this.set_field_invalid("surname-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Surname is required.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid("surname-field");
      return true;
    },
    validate_id_number() {
      if (!this.candidate_data.id_number) {
        this.set_field_invalid("id-number-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "ID number is required.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      if (!this.validate_id(this.candidate_data.id_number)) {
        this.set_field_invalid("id-number-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Invalid ID number format.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid("id-number-field");
      return true;
    },
    validate_date_of_birth() {
      if (!this.candidate_data.date_of_birth) {
        this.set_field_invalid("date-of-birth-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Date of birth is required.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid("date-of-birth-field");
      return true;
    },
    validate_race_code() {
      if (this.criminal_check_included && !this.candidate_data.race_code) {
        this.set_field_invalid("race-code-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Race code is required for criminal checks.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      if (this.academic_checks_included && !this.candidate_data.race_code) {
        this.set_field_invalid("race-code-field");
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Race code is required for academic checks.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid("race-code-field");
      return true;
    },
    set_field_invalid(field_id) {
      const el = document.getElementById(field_id);
      if (el) el.classList.add("border-danger");
    },
    set_field_valid(field_id) {
      const el = document.getElementById(field_id);
      if (el) el.classList.remove("border-danger");
    },
    validate_information() {
      const first = this.validate_first_name();
      if (!first) return false;
      const surname = this.validate_surname();
      if (!surname) return false;
      const id = this.validate_id_number();
      if (!id) return false;
      const dob = this.validate_date_of_birth();
      if (!dob) return false;
      const race = this.validate_race_code();
      if (!race) return false;
      return true;
    },
    handle_data_submit() {
      if (this.validate_information()) {
        if (this.candidate_data.date_of_birth) {
          const [year, month, day] =
            this.candidate_data.date_of_birth.split("-");
          if (year && month && day) {
            this.candidate_data.date_of_birth = `${day}/${month}/${year}`;
          }
        }
        this.$emit("update_data", "candidate", this.candidate_data);
        return true;
      }
      return false;
    },
    fetch_data() {
      const token = localStorage.getItem("token");
      const workflow_id = localStorage.getItem("workflow_id") || null;
      if (!workflow_id) {
        console.error("Workflow ID is not set in localStorage.");
        return;
      }

      const params = new URLSearchParams({
        workflow_id: workflow_id,
        service_id: this.service_id,
      });

      fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/get_sections_by_workflow_client/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (
            data.status === "success" &&
            data.data &&
            data.data.sections &&
            data.data.sections.length > 0
          ) {
            // Find the section for Candidate Screening (or use the first section)
            const section =
              data.data.sections.find(
                (s) => s.service_name === "Candidate Screening"
              ) || data.data.sections[0];

            // Flatten key_mappings into an object
            let candidate = {};
            let key_group = {};
            if (
              section &&
              section.key_groups &&
              section.key_groups.length > 0 &&
              section.key_groups[0].key_mappings
            ) {
              key_group = section.key_groups[0];
              section.key_groups[0].key_mappings.forEach((mapping) => {
                candidate[mapping.key_name] = mapping.key_value;
              });
            }

            // Set candidate data
            this.candidate_data.id = key_group.id || "";
            this.candidate_data.first_name = candidate.first_name || "";
            this.candidate_data.surname = candidate.surname || "";
            this.candidate_data.date_of_birth = candidate.date_of_birth || "";
            this.candidate_data.id_number = candidate.id_number || "";
            this.candidate_data.maiden_name = candidate.maiden_name || "";
            this.candidate_data.other_names = candidate.other_names || "";
            this.candidate_data.passport_number =
              candidate.passport_number || "";
            this.candidate_data.race_code = candidate.race_code || "";
          }
        })
        .catch((error) => {
          console.error("Error fetching candidate data:", error);
        });
    },
    set_customer_data(customer_data) {
      if (customer_data && Object.keys(customer_data).length > 0 && this.candidate_data.id === "") {
        this.candidate_data.first_name = customer_data.first_name || "";
        this.candidate_data.surname = customer_data.surname ||
          customer_data.last_name ||
          "";
        this.candidate_data.id_number = customer_data.id_number || "";
        if (customer_data.date_of_birth) {
          const dob = customer_data.date_of_birth;
          if (/^\d{8}$/.test(dob)) {
            const year = dob.slice(0, 4);
            const month = dob.slice(4, 6);
            const day = dob.slice(6, 8);
            this.candidate_data.date_of_birth = `${year}-${month}-${day}`;
          } else {
            this.candidate_data.date_of_birth = dob;
          }
        }
      }
    },
  },
  mounted() {
    this.fetch_data();
  },
  watch: {
    "candidate_data.id_number": {
      handler(new_value) {
        if (/^\d{13}$/.test(new_value)) {
          const yy = new_value.slice(0, 2);
          const mm = new_value.slice(2, 4);
          const dd = new_value.slice(4, 6);

          // Determine century: if year > current year's last two digits, assume 1900s else 2000s
          const current_two = new Date().getFullYear() % 100;
          const century_prefix = parseInt(yy, 10) > current_two ? "19" : "20";
          const full_year = `${century_prefix}${yy}`;
          // Format as YYYY-MM-DD for input type="date"
          this.candidate_data.date_of_birth = `${full_year}-${mm}-${dd}`;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.service-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.form-label {
  margin-bottom: 0.18rem;
}

.form-control-sm {
  padding: 0.375rem 0.5rem;
}
</style>
